/*
 * NAGOYA — composants des pages de collaboration (sans dépendance, chargé en defer)
 * <nagoya-planches> : galerie glissable + vue agrandie dans un <dialog>
 * <nagoya-film>     : film principal (son, sous-titres) ou boucle muette avec bouton Pause / Lecture ;
 *                     sert aussi aux vidéos en boucle du hero de collaboration, des motifs et des étapes de l'atelier
 * Libellés : attributs data-label-* rendus par les sections avec des clés de traduction ({{ 'nagoya.collab.…' | t }}),
 * repli en français si un attribut manque. Écouteurs liés à un AbortController : rien ne reste attaché quand
 * l'éditeur de thème retire ou déplace une section.
 * Détails : docs/motion-design.md
 */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;

  /* ---------- Planches : galerie glissable et vue agrandie ---------- */

  if (!customElements.get('nagoya-planches')) {
    customElements.define(
      'nagoya-planches',
      class extends HTMLElement {
        connectedCallback() {
          this.ac?.abort();
          this.ac = new AbortController();
          const o = { signal: this.ac.signal };
          const $ = (r) => this.querySelector(`[data-ref="${r}"]`);

          this.piste = $('piste');
          this.dialog = $('dialog');
          this.grande = $('grande');
          this.legende = $('grande-legende');
          this.compteur = $('compteur');
          if (!this.piste || !this.dialog || !this.grande) return;

          this.items = [...this.querySelectorAll('[data-ref="ouvrir"]:not([disabled])')];
          this.index = 0;
          this.dragged = false;

          // Une seule planche : ni précédente ni suivante
          const seule = this.items.length < 2;
          ['precedente', 'suivante', 'compteur'].forEach((r) => $(r)?.toggleAttribute('hidden', seule));

          this.items.forEach((b, i) =>
            b.addEventListener('click', (e) => (this.dragged ? e.preventDefault() : this.open(i)), o)
          );
          $('precedente')?.addEventListener('click', () => this.show(this.index - 1), o);
          $('suivante')?.addEventListener('click', () => this.show(this.index + 1), o);
          $('fermer')?.addEventListener('click', () => this.dialog.close(), o);
          // Clic sur le fond : fermeture
          this.dialog.addEventListener('click', (e) => e.target === this.dialog && this.dialog.close(), o);
          this.dialog.addEventListener(
            'keydown',
            (e) => {
              if (e.key === 'ArrowLeft') this.show(this.index - 1);
              if (e.key === 'ArrowRight') this.show(this.index + 1);
            },
            o
          );
          this.dialog.addEventListener('close', () => this.items[this.index]?.focus({ preventScroll: true }), o);

          // Glisser à la souris (le tactile et le clavier défilent nativement). Le pointeur n'est capturé qu'au-delà
          // de 5 px : capturé dès l'appui, le clic partirait vers la piste et n'ouvrirait plus la planche.
          let x0 = 0;
          let left0 = 0;
          let pointer = null;
          const fin = () => {
            if (pointer === null) return;
            pointer = null;
            this.piste.classList.remove('is-dragging');
            // Le clic qui suit un glisser n'ouvre pas la planche
            setTimeout(() => (this.dragged = false));
          };
          this.piste.addEventListener(
            'pointerdown',
            (e) => {
              if (e.pointerType !== 'mouse' || e.button !== 0) return;
              pointer = e.pointerId;
              x0 = e.clientX;
              left0 = this.piste.scrollLeft;
              this.dragged = false;
            },
            o
          );
          this.piste.addEventListener(
            'pointermove',
            (e) => {
              if (pointer !== e.pointerId) return;
              // Bouton relâché hors de la piste avant le seuil : le glisser s'arrête
              if (!(e.buttons & 1)) return fin();
              const dx = e.clientX - x0;
              if (!this.dragged && Math.abs(dx) > 5) {
                this.dragged = true;
                this.piste.classList.add('is-dragging');
                try {
                  this.piste.setPointerCapture(e.pointerId);
                } catch {
                  /* pointeur déjà relâché */
                }
              }
              if (this.dragged) this.piste.scrollLeft = left0 - dx;
            },
            o
          );
          ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((t) => this.piste.addEventListener(t, fin, o));
        }

        disconnectedCallback() {
          this.ac?.abort();
        }

        open(i) {
          this.show(i);
          this.dialog.showModal();
        }

        show(i) {
          const n = this.items.length;
          if (!n) return;
          this.index = (i + n) % n;
          const b = this.items[this.index];
          const g = this.grande;
          // L'ancienne planche disparaît d'abord : jamais une image sous la légende d'une autre
          g.removeAttribute('src');
          g.removeAttribute('srcset');
          if (b.dataset.width && b.dataset.height) {
            g.width = +b.dataset.width;
            g.height = +b.dataset.height;
          }
          g.sizes = '(min-width: 72rem) 70rem, calc(100vw - 2rem)';
          if (b.dataset.srcset) g.srcset = b.dataset.srcset;
          g.src = b.dataset.src;
          g.alt = b.dataset.alt || '';
          if (this.legende) this.legende.textContent = b.dataset.legende || '';
          if (this.compteur) this.compteur.textContent = `${this.index + 1} / ${n}`;
        }
      }
    );
  }

  /* ---------- Film et boucles vidéo ---------- */

  if (!customElements.get('nagoya-film')) {
    customElements.define(
      'nagoya-film',
      class extends HTMLElement {
        connectedCallback() {
          const $ = (r) => this.querySelector(`[data-ref="${r}"]`);
          // Les sections qui passent par video_tag n'ont pas de data-ref sur la vidéo
          this.video = $('video') || this.querySelector('video');
          if (!this.video) return;

          this.ac?.abort();
          this.ac = new AbortController();
          const o = { signal: this.ac.signal };

          this.pauseBtn = $('pause');
          this.sonBtn = $('son');
          this.stBtn = $('soustitres');
          this.loop = this.dataset.mode === 'boucle';
          // Calque d'une séquence de motifs : la boucle n'est lue que s'il est découvert
          this.layer = this.closest('[data-nagoya-layer]');
          // null tant que le visiteur n'a rien demandé ; 'lecture' ou 'pause' ensuite
          this.choix = null;

          $('lancer')?.addEventListener(
            'click',
            () => {
              this.classList.add('is-started');
              this.video.muted = false;
              this.video.controls = true;
              this.play();
              this.sync();
              // Le bouton de lancement disparaît : le focus passe au bouton Pause, désormais visible
              (this.pauseBtn || this.video).focus({ preventScroll: true });
            },
            o
          );

          this.pauseBtn?.addEventListener(
            'click',
            () => {
              if (this.video.paused) {
                this.choix = 'lecture';
                this.play();
              } else {
                this.choix = 'pause';
                this.video.pause();
              }
            },
            o
          );

          this.sonBtn?.addEventListener(
            'click',
            () => {
              this.video.muted = !this.video.muted;
              this.sync();
            },
            o
          );

          this.stBtn?.addEventListener(
            'click',
            () => {
              const track = this.video.textTracks[0];
              if (!track) return;
              track.mode = track.mode === 'showing' ? 'hidden' : 'showing';
              this.sync();
            },
            o
          );

          ['play', 'pause', 'volumechange'].forEach((t) => this.video.addEventListener(t, () => this.sync(), o));

          // Boucle muette : lecture seulement à l'écran, jamais seule en mouvement réduit
          if (this.loop) {
            this.classList.add('is-started');
            this.io?.disconnect();
            this.io = new IntersectionObserver(([e]) => {
              this.visible = e.isIntersecting;
              this.update();
            });
            this.io.observe(this);
            this.mo?.disconnect();
            this.mo = null;
            if (this.layer) {
              this.mo = new MutationObserver(() => this.update());
              this.mo.observe(this.layer, { attributes: true, attributeFilter: ['class'] });
            }
            reduce.addEventListener('change', () => this.update(), o);
          }

          this.sync();
        }

        disconnectedCallback() {
          this.ac?.abort();
          this.io?.disconnect();
          this.mo?.disconnect();
          this.io = null;
          this.mo = null;
        }

        update() {
          if (!this.video) return;
          // Calque masqué (séquence pilotée par nagoya-motion.js) : rien ne joue sous le masque
          const decouvert =
            !this.layer || this.layer.classList.contains('is-shown') || !root.classList.contains('nagoya-js');
          // Sans demande du visiteur : lecture automatique seulement hors mouvement réduit
          const voulue = this.choix ? this.choix === 'lecture' : !reduce.matches;
          if (voulue && this.visible && decouvert) this.play();
          else if (!this.video.paused) this.video.pause();
        }

        play() {
          const p = this.video.play();
          if (p && p.catch) p.catch(() => {});
        }

        sync() {
          const d = this.dataset;
          if (this.pauseBtn) {
            const enLecture = !this.video.paused;
            const libelle = this.pauseBtn.querySelector('[data-ref="libelle"]') || this.pauseBtn;
            libelle.textContent = enLecture ? d.labelPause || 'Pause' : d.labelPlay || 'Lecture';
            this.pauseBtn.dataset.etat = enLecture ? 'lecture' : 'pause';
          }
          if (this.sonBtn) {
            this.sonBtn.textContent = this.video.muted
              ? d.labelUnmute || 'Activer le son'
              : d.labelMute || 'Couper le son';
          }
          if (this.stBtn) this.stBtn.setAttribute('aria-pressed', String(this.video.textTracks[0]?.mode === 'showing'));
        }
      }
    );
  }
})();
